import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"
import { CarRouteConfig, EmissionType, TravelMode } from "../api/adHocCommands"
import {
  co2ClassLabel,
  co2ClassOptions,
  emissionOptions,
  optionLabel,
  travelModes,
} from "./maps/carRouteOptions"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  selectIsHereRouting,
  selectIsPtvRouting,
} from "@/reducers/serviceConfigSelectors"
import { Label } from "@/components/ui/label"
import { NumberInput } from "@/components/ui/number-input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

export interface CarConfigSelectorHandle {
  getPendingConfig: () => Partial<CarRouteConfig>
  clearAll: () => void
  saveConfig: () => Promise<void>
  applyConfig: (config: Partial<CarRouteConfig>) => void
  hasChanges: boolean
}

type CarRouteConfigSelectorProps = {
  currentConfig?: CarRouteConfig
  onConfigChange?: (config: Partial<CarRouteConfig>) => void
  onClearConfig?: () => void
  isLoading?: boolean
  disabled?: boolean
  onHasChangesChange?: (hasChanges: boolean) => void
}

// Maximum values for dimensions
const MAX_DIMENSION = 50 // max 50 meters for width, height, length
const MAX_WEIGHT = 50000 // max 50 tons (50,000 kg)
const MAX_FUEL_CONSUMPTION = 100 // max 100 l/100km, matches backend validation

const CarConfigSelector = forwardRef<
  CarConfigSelectorHandle,
  CarRouteConfigSelectorProps
>(
  (
    {
      currentConfig = {},
      onConfigChange,
      onClearConfig,
      isLoading = false,
      disabled = false,
      onHasChangesChange,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const isHereRouting = useSelector(selectIsHereRouting)
    const isPtvRouting = useSelector(selectIsPtvRouting)
    const showEmissionFields = isHereRouting || isPtvRouting

    const [width, setWidth] = useState<string | undefined>(currentConfig.width)
    const [height, setHeight] = useState<string | undefined>(
      currentConfig.height,
    )
    const [length, setLength] = useState<string | undefined>(
      currentConfig.length,
    )
    const [weight, setWeight] = useState<string | undefined>(
      currentConfig.weight,
    )
    const [maxSpeed, setMaxSpeed] = useState<string | undefined>(
      currentConfig.maxSpeed,
    )
    const [travelMode, setTravelMode] = useState<TravelMode | undefined>(
      currentConfig.travelMode || "car", // Default to "car"
    )

    const [emissionType, setEmissionType] = useState<EmissionType | undefined>(
      currentConfig?.emissionType || undefined,
    )

    const [axleCount, setAxleCount] = useState<string | undefined>(
      currentConfig.axleCount,
    )

    const [co2class, setCo2class] = useState<string | undefined>(
      currentConfig.co2class || undefined,
    )

    const [fuelConsumption, setFuelConsumption] = useState<string | undefined>(
      currentConfig.fuelConsumption,
    )

    const [hasChanges, setHasChanges] = useState(false)
    const [saving, setSaving] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)

    // Update local state when currentConfig changes from parent
    useEffect(() => {
      // Update all the state values from current config on initial load and when config refreshes from server
      if (!isLoading && !saving) {
        // Update all the state values from current config
        setWidth(currentConfig.width)
        setHeight(currentConfig.height)
        setLength(currentConfig.length)
        setWeight(currentConfig.weight)
        setMaxSpeed(currentConfig.maxSpeed)
        if (!isInitialized || currentConfig.travelMode) {
          setTravelMode(currentConfig.travelMode || "car")
        }

        if (!isInitialized || currentConfig.emissionType) {
          setEmissionType(currentConfig.emissionType || undefined)
        }

        setAxleCount(currentConfig.axleCount)
        if (!isInitialized || currentConfig.co2class) {
          setCo2class(currentConfig.co2class || undefined)
        }

        setFuelConsumption(currentConfig.fuelConsumption)

        if (!isInitialized) {
          setHasChanges(false)
          onHasChangesChange?.(false)
          setIsInitialized(true)
        }
      }

      // Handle post-save state reset
      if (!isLoading && saving) {
        setHasChanges(false)
        onHasChangesChange?.(false)
      }
    }, [currentConfig, isLoading, onHasChangesChange, isInitialized, saving])

    const markAsChanged = useCallback(() => {
      if (!hasChanges) {
        setHasChanges(true)
        onHasChangesChange?.(true)
      }
    }, [hasChanges, onHasChangesChange])

    const saveConfig = useCallback(async () => {
      const config: Partial<CarRouteConfig> = {
        width,
        height,
        length,
        weight,
        maxSpeed,
        travelMode,
        emissionType,
        axleCount,
        co2class,
        fuelConsumption,
      }

      // Show the saving spinner
      setSaving(true)

      try {
        await onConfigChange?.(config)

        // Don't update initialConfig here - let the parent component
        // refresh the data from the server which will trigger a proper update
        // through the currentConfig prop

        // Set hasChanges to false
        setHasChanges(false)
        onHasChangesChange?.(false)
      } catch (error) {
        console.error("Failed to save vehicle configuration:", error)

        // Show error notification if possible
        if (window.appToaster) {
          window.appToaster.show({
            message: t("maps.carConfigSelector.saveFailed"),
            intent: "danger",
            timeout: 3000,
          })
        }
      } finally {
        setSaving(false)
      }
    }, [
      emissionType,
      height,
      length,
      maxSpeed,
      onConfigChange,
      onHasChangesChange,
      travelMode,
      weight,
      width,
      axleCount,
      co2class,
      fuelConsumption,
      t,
    ])

    // Clear all form fields
    const handleClearConfig = useCallback(() => {
      // Clear all local state values
      setWidth(undefined)
      setHeight(undefined)
      setLength(undefined)
      setWeight(undefined)
      setMaxSpeed(undefined)
      setTravelMode("car") // Reset to car but don't clear it completely
      setEmissionType(undefined)
      setAxleCount(undefined)
      setCo2class(undefined)
      setFuelConsumption(undefined)

      // Call the parent's onClearConfig handler if provided
      if (onClearConfig) {
        onClearConfig()
      }

      // Set hasChanges to true since we've made changes
      setHasChanges(true)
      onHasChangesChange?.(true)
    }, [onClearConfig, onHasChangesChange])

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getPendingConfig: () => ({
          width,
          height,
          length,
          weight,
          maxSpeed,
          travelMode,
          emissionType,
          axleCount,
          co2class,
          fuelConsumption,
        }),
        clearAll: handleClearConfig,
        saveConfig,
        applyConfig: (config: Partial<CarRouteConfig>) => {
          // Overwrite every field — fields the incoming config omits are
          // cleared. Caller is expected to pass a full preset.
          setWidth(config.width)
          setHeight(config.height)
          setLength(config.length)
          setWeight(config.weight)
          setMaxSpeed(config.maxSpeed)
          setTravelMode(config.travelMode)
          setEmissionType(config.emissionType)
          setAxleCount(config.axleCount)
          setCo2class(config.co2class)
          setFuelConsumption(config.fuelConsumption)
          // Intentionally do NOT call onConfigChange here — in this component
          // onConfigChange is the save callback (invoked from saveConfig), not a
          // change callback. Keeping the preset as pending state until the user
          // clicks Save matches the existing flow.
          markAsChanged()
        },
        hasChanges,
      }),
      [
        width,
        height,
        length,
        weight,
        maxSpeed,
        travelMode,
        emissionType,
        axleCount,
        co2class,
        fuelConsumption,
        handleClearConfig,
        saveConfig,
        markAsChanged,
        hasChanges,
      ],
    )

    const handleDimensionChange = (
      dimension: "width" | "height" | "length" | "weight" | "maxSpeed",
      value?: string,
    ) => {
      markAsChanged()
      let updatedValue = value

      // Skip empty/undefined values
      if (value === undefined || value === "") {
        switch (dimension) {
          case "width":
            setWidth(undefined)
            break
          case "height":
            setHeight(undefined)
            break
          case "length":
            setLength(undefined)
            break
          case "weight":
            setWeight(undefined)
            break
          case "maxSpeed":
            setMaxSpeed(undefined)
        }
        return
      }

      // Parse the numeric value
      const numericValue = parseFloat(value)

      // Validate the value is positive
      if (isNaN(numericValue) || numericValue <= 0) {
        // Don't clear the value - just keep the previous value
        return
      }

      // Apply specific validation based on dimension
      if (
        dimension === "width" ||
        dimension === "height" ||
        dimension === "length"
      ) {
        // Ensure dimensions are not larger than MAX_DIMENSION (50m)
        if (numericValue > MAX_DIMENSION) {
          updatedValue = MAX_DIMENSION.toString()
        } else {
          // Preserve the exact string value user entered
          updatedValue = value
        }
      } else if (dimension === "weight") {
        // Ensure weight is not larger than MAX_WEIGHT (50,000kg)
        if (numericValue > MAX_WEIGHT) {
          updatedValue = MAX_WEIGHT.toString()
        } else {
          // Ensure weight is a whole number but preserve user input otherwise
          updatedValue = Math.floor(numericValue).toString()
        }
      } else if (dimension === "maxSpeed") {
        // Ensure max speed is a whole number but preserve user input otherwise
        updatedValue = Math.floor(numericValue).toString()
      }

      // Update the appropriate state
      switch (dimension) {
        case "width":
          setWidth(updatedValue)
          break
        case "height":
          setHeight(updatedValue)
          break
        case "length":
          setLength(updatedValue)
          break
        case "weight":
          setWeight(updatedValue)
          break
        case "maxSpeed":
          setMaxSpeed(updatedValue)
      }
    }

    const handleFuelConsumptionChange = (value?: string) => {
      markAsChanged()

      // Skip empty/undefined values
      if (value === undefined || value === "") {
        setFuelConsumption(undefined)
        return
      }

      // Parse the numeric value
      const numericValue = parseFloat(value)

      // Validate the value is positive and within range
      if (
        isNaN(numericValue) ||
        numericValue <= 0 ||
        numericValue > MAX_FUEL_CONSUMPTION
      ) {
        // Don't clear the value - just keep the previous value
        return
      }

      setFuelConsumption(value)
    }

    const handleAxleCountChange = (value?: string) => {
      markAsChanged()

      // Skip empty/undefined values
      if (value === undefined || value === "") {
        setAxleCount(undefined)
        return
      }

      // Parse the numeric value
      const numericValue = parseFloat(value)

      // Validate the value is positive and within range
      if (isNaN(numericValue) || numericValue < 2 || numericValue > 20) {
        // Don't clear the value - just keep the previous value
        return
      }

      // Ensure axle count is a whole number
      const updatedValue = Math.floor(numericValue).toString()
      setAxleCount(updatedValue)
    }

    // Loading spinner component
    const UpdateIndicator = () => {
      return (
        <div className="absolute left-1/2 top-1/2 z-[1000] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg p-4">
          <Loader2 size={25} className="animate-spin" />
        </div>
      )
    }

    const isFormDisabled = disabled || isLoading || saving

    return (
      <div
        className={cn("relative w-full space-y-4", {
          "opacity-70": isFormDisabled,
        })}
      >
        <div className="space-y-2">
          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label htmlFor="length">
                {t("maps.carConfigSelector.length")}
              </Label>
              <NumberInput
                id="length"
                disabled={isFormDisabled}
                value={length ? parseFloat(length) : undefined}
                onValueChange={(value) =>
                  handleDimensionChange("length", value?.toString())
                }
                min={0.01}
                max={MAX_DIMENSION}
                stepper={0.01}
                decimalScale={2}
                fixedDecimalScale
                placeholder={t("maps.carConfigSelector.enterLength")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="width">{t("maps.carConfigSelector.width")}</Label>
              <NumberInput
                id="width"
                disabled={isFormDisabled}
                value={width ? parseFloat(width) : undefined}
                onValueChange={(value) =>
                  handleDimensionChange("width", value?.toString())
                }
                min={0.01}
                max={MAX_DIMENSION}
                stepper={0.01}
                decimalScale={2}
                fixedDecimalScale
                placeholder={t("maps.carConfigSelector.enterWidth")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height">
                {t("maps.carConfigSelector.height")}
              </Label>
              <NumberInput
                id="height"
                disabled={isFormDisabled}
                value={height ? parseFloat(height) : undefined}
                onValueChange={(value) =>
                  handleDimensionChange("height", value?.toString())
                }
                min={0.01}
                max={MAX_DIMENSION}
                stepper={0.01}
                decimalScale={2}
                fixedDecimalScale
                placeholder={t("maps.carConfigSelector.enterHeight")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="weight">
                {t("maps.carConfigSelector.weight")}
              </Label>
              <NumberInput
                id="weight"
                disabled={isFormDisabled}
                value={weight ? parseFloat(weight) : undefined}
                onValueChange={(value) =>
                  handleDimensionChange("weight", value?.toString())
                }
                min={1}
                max={MAX_WEIGHT}
                stepper={100}
                decimalScale={0}
                placeholder={t("maps.carConfigSelector.enterWeight")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxSpeed">
                {t("maps.carConfigSelector.maxSpeed")}
              </Label>
              <NumberInput
                id="maxSpeed"
                disabled={isFormDisabled}
                value={maxSpeed ? parseFloat(maxSpeed) : undefined}
                onValueChange={(value) =>
                  handleDimensionChange("maxSpeed", value?.toString())
                }
                min={1}
                stepper={1}
                decimalScale={0}
                placeholder={t("maps.carConfigSelector.enterMaxSpeed")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="axleCount">
                {t("maps.carConfigSelector.axleCount")}
              </Label>
              <NumberInput
                id="axleCount"
                disabled={isFormDisabled}
                value={axleCount ? parseFloat(axleCount) : undefined}
                onValueChange={(value) =>
                  handleAxleCountChange(value?.toString())
                }
                min={2}
                max={20}
                stepper={1}
                decimalScale={0}
                placeholder={t("maps.carConfigSelector.enterAxleCount")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fuelConsumption">
                {t("maps.carConfigSelector.fuelConsumption")}
              </Label>
              <NumberInput
                id="fuelConsumption"
                disabled={isFormDisabled}
                value={
                  fuelConsumption ? parseFloat(fuelConsumption) : undefined
                }
                onValueChange={(value) =>
                  handleFuelConsumptionChange(value?.toString())
                }
                min={0.1}
                max={MAX_FUEL_CONSUMPTION}
                stepper={0.5}
                decimalScale={1}
                placeholder={t("maps.carConfigSelector.enterFuelConsumption")}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="travelMode">
              {t("maps.carConfigSelector.travelMode")}
            </Label>
            <Select
              disabled={isFormDisabled}
              value={travelMode || ""}
              onValueChange={(value: TravelMode) => {
                markAsChanged()
                setTravelMode(value)
              }}
            >
              <SelectTrigger id="travelMode" className="w-full md:w-[240px]">
                <SelectValue
                  placeholder={t("maps.carConfigSelector.selectTravelMode")}
                />
              </SelectTrigger>
              <SelectContent>
                {travelModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {optionLabel(t, mode)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showEmissionFields && (
            <>
              <div className="space-y-2">
                <Label htmlFor="emissionType">
                  {t("maps.carConfigSelector.emissionType")}
                </Label>
                <Select
                  disabled={isFormDisabled}
                  value={emissionType || ""}
                  onValueChange={(value: EmissionType) => {
                    markAsChanged()
                    setEmissionType(value === "none" ? undefined : value)
                  }}
                >
                  <SelectTrigger
                    id="emissionType"
                    className="w-full md:w-[240px]"
                  >
                    <SelectValue
                      placeholder={t(
                        "maps.carConfigSelector.selectEmissionType",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {emissionOptions.map((emission) => (
                      <SelectItem key={emission.value} value={emission.value}>
                        {optionLabel(t, emission)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="co2class">
                  {t("maps.carConfigSelector.co2Class")}
                </Label>
                <Select
                  disabled={isFormDisabled}
                  value={co2class || ""}
                  onValueChange={(value: string) => {
                    markAsChanged()
                    setCo2class(value === "none" ? undefined : value)
                  }}
                >
                  <SelectTrigger id="co2class" className="w-full md:w-[240px]">
                    <SelectValue
                      placeholder={t("maps.carConfigSelector.selectCo2Class")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {co2ClassOptions.map((co2Class) => (
                      <SelectItem key={co2Class.value} value={co2Class.value}>
                        {co2ClassLabel(t, co2Class)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </div>
    )
  },
)

CarConfigSelector.displayName = "CarConfigSelector"

export default CarConfigSelector
